import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	Box,
	Button,
	Grid2,
	IconButton,
	TextField,
	Typography,
} from "@mui/material";
import { Control, Controller, useFieldArray } from "react-hook-form";
import { TolerancePicker } from "@/controls/TolerancePicker";
import { Clip, CLIP_PUNCH_SLOT_EMPTY } from "@/shared/models/Clip";
import { parseNumberInput } from "@/shared/utils/parseNumberInput";

type Props = {
	control: Control<Clip>;
	disabled: boolean;
};

export const ClipPunchSlotsField = ({ control, disabled }: Props) => {
	const { fields, append, remove } = useFieldArray({ control, name: "Slots" });

	const handleAdd = () => {
		append({ ...CLIP_PUNCH_SLOT_EMPTY });
	};

	return (
		<Grid2 size={{ xs: 12 }}>
			{/* Column headers */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 0.75,
					px: 0.5,
					mb: 0.5,
				}}
			>
				<Typography variant="caption" color="text.secondary" sx={{ flex: 0.7 }}>
					Hole Dia.
				</Typography>
				<Typography variant="caption" color="text.secondary" sx={{ flex: 1.4 }}>
					Tolerance
				</Typography>
				<Typography variant="caption" color="text.secondary" sx={{ flex: 2.2 }}>
					Punch Part #
				</Typography>
				<Typography variant="caption" color="text.secondary" sx={{ flex: 1.3 }}>
					Button Part #
				</Typography>
				<Typography variant="caption" color="text.secondary" sx={{ flex: 0.5 }}>
					Qty
				</Typography>
				<Box sx={{ width: 32, flexShrink: 0 }} />
			</Box>

			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
				{fields.map((field, index) => (
					<Box
						key={field.id}
						sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
					>
						<Box sx={{ flex: 0.7 }}>
							<Controller
								name={`Slots.${index}.HoleDia`}
								control={control}
								render={({ field: f }) => (
									<TextField
										type="number"
										value={f.value ?? ""}
										onChange={(e) =>
											f.onChange(
												parseNumberInput(e.target.value, {
													min: 0,
													max: 9.9999,
												}),
											)
										}
										fullWidth
										size="small"
										disabled={disabled}
										InputProps={{ readOnly: disabled }}
										inputProps={{ min: 0, max: 9.9999, step: "0.0001" }}
									/>
								)}
							/>
						</Box>
						<Box sx={{ flex: 1.4 }}>
							<Controller
								name={`Slots.${index}.Tolerance`}
								control={control}
								render={({ field: f }) => (
									<TolerancePicker
										value={f.value}
										onChange={(v) => f.onChange(v)}
										disabled={disabled}
										emptyOnNull
									/>
								)}
							/>
						</Box>
						<Box sx={{ flex: 2.2 }}>
							<Controller
								name={`Slots.${index}.PunchPartNumber`}
								control={control}
								render={({ field: f }) => (
									<TextField
										value={f.value || ""}
										onChange={(e) => f.onChange(e.target.value || null)}
										fullWidth
										size="small"
										disabled={disabled}
										InputProps={{ readOnly: disabled }}
									/>
								)}
							/>
						</Box>
						<Box sx={{ flex: 1.3 }}>
							<Controller
								name={`Slots.${index}.ButtonPartNumber`}
								control={control}
								render={({ field: f }) => (
									<TextField
										value={f.value || ""}
										onChange={(e) => f.onChange(e.target.value || null)}
										fullWidth
										size="small"
										disabled={disabled}
										InputProps={{ readOnly: disabled }}
									/>
								)}
							/>
						</Box>
						<Box sx={{ flex: 0.5 }}>
							<Controller
								name={`Slots.${index}.Quantity`}
								control={control}
								render={({ field: f }) => (
									<TextField
										type="number"
										value={f.value ?? ""}
										onChange={(e) =>
											f.onChange(
												parseNumberInput(e.target.value, {
													integer: true,
													min: 1,
													max: 50,
												}),
											)
										}
										fullWidth
										size="small"
										disabled={disabled}
										InputProps={{ readOnly: disabled }}
										inputProps={{ min: 1, max: 50, step: 1 }}
									/>
								)}
							/>
						</Box>
						<IconButton
							size="small"
							color="secondary"
							onClick={() => remove(index)}
							disabled={disabled || fields.length <= 1}
							sx={{
								flexShrink: 0,
								visibility: disabled ? "hidden" : "visible",
							}}
						>
							<DeleteIcon fontSize="small" />
						</IconButton>
					</Box>
				))}
			</Box>

			{!disabled && (
				<Box sx={{ mt: 1 }}>
					<Button
						size="small"
						variant="outlined"
						startIcon={<AddIcon />}
						onClick={handleAdd}
					>
						Add Row
					</Button>
				</Box>
			)}
		</Grid2>
	);
};
